import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUserOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  MessageSquare, 
  Plus, 
  Search, 
  Filter,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  HelpCircle,
  FileText,
  Phone,
  Mail,
  MessageCircle,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Star,
  Download,
  Share2,
  Bookmark,
  Eye,
  Edit,
  Trash2,
  Send,
  Reply,
  Forward,
  Archive,
  Tag,
  User,
  Calendar,
  AlertTriangle
} from "lucide-react";

interface SupportTicket {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'in-progress' | 'waiting' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'technical' | 'billing' | 'feature-request' | 'bug-report' | 'general';
  assignedTo?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastResponse?: string;
  responseCount: number;
  attachments: string[];
  tags: string[];
  isEscalated?: boolean;
}

interface TicketResponse {
  id: string;
  ticketId: string;
  author: string;
  content: string;
  timestamp: string;
  isInternal: boolean;
  attachments: string[];
}

const mockTickets: SupportTicket[] = [
  {
    id: 'T-001',
    title: 'DNS Policy Not Working After Update',
    description: 'After updating to the latest version, our custom DNS policy for blocking social media sites is not working properly. Users can still access blocked domains.',
    status: 'in-progress',
    priority: 'high',
    category: 'technical',
    assignedTo: 'support@fendly.com',
    createdBy: 'admin@example.com',
    createdAt: '2024-01-20T10:30:00Z',
    updatedAt: '2024-01-20T14:45:00Z',
    lastResponse: '2024-01-20T14:45:00Z',
    responseCount: 3,
    attachments: ['policy-config.json', 'error-logs.txt'],
    tags: ['dns-policy', 'blocking', 'update-issue']
  },
  {
    id: 'T-002',
    title: 'Request for Additional User Licenses',
    description: 'We need to add 5 more users to our organization. Please provide pricing and activation instructions.',
    status: 'open',
    priority: 'medium',
    category: 'billing',
    createdBy: 'admin@example.com',
    createdAt: '2024-01-19T15:20:00Z',
    updatedAt: '2024-01-19T15:20:00Z',
    responseCount: 1,
    attachments: [],
    tags: ['licensing', 'users', 'billing']
  },
  {
    id: 'T-003',
    title: 'Feature Request: Time-based Policies',
    description: 'It would be great to have the ability to set policies that are active only during certain hours (e.g., block gaming sites during work hours).',
    status: 'waiting',
    priority: 'low',
    category: 'feature-request',
    createdBy: 'manager@example.com',
    createdAt: '2024-01-18T09:15:00Z',
    updatedAt: '2024-01-18T09:15:00Z',
    lastResponse: '2024-01-18T16:30:00Z',
    responseCount: 2,
    attachments: [],
    tags: ['feature-request', 'time-based', 'policies']
  },
  {
    id: 'T-004',
    title: 'False Positive: Legitimate Business Site Blocked',
    description: 'Our company website (business.example.com) is being blocked as malicious. This is a legitimate business site that we need access to.',
    status: 'resolved',
    priority: 'high',
    category: 'bug-report',
    assignedTo: 'support@fendly.com',
    createdBy: 'user@example.com',
    createdAt: '2024-01-17T11:45:00Z',
    updatedAt: '2024-01-18T10:20:00Z',
    lastResponse: '2024-01-18T10:20:00Z',
    responseCount: 4,
    attachments: ['site-screenshot.png'],
    tags: ['false-positive', 'whitelist', 'business-site']
  },
  {
    id: 'T-005',
    title: 'Performance Issues During Peak Hours',
    description: 'We\'re experiencing slow DNS resolution and timeouts during business hours (9 AM - 5 PM). Response times are 5-10 seconds instead of the usual 100ms.',
    status: 'open',
    priority: 'urgent',
    category: 'technical',
    assignedTo: 'support@fendly.com',
    createdBy: 'admin@example.com',
    createdAt: '2024-01-16T14:30:00Z',
    updatedAt: '2024-01-20T09:15:00Z',
    lastResponse: '2024-01-20T09:15:00Z',
    responseCount: 5,
    attachments: ['performance-metrics.csv', 'network-config.txt'],
    tags: ['performance', 'dns-resolution', 'timeouts', 'peak-hours'],
    isEscalated: true
  }
];

const mockResponses: TicketResponse[] = [
  {
    id: 'R-001',
    ticketId: 'T-001',
    author: 'support@fendly.com',
    content: 'Thank you for reporting this issue. We\'ve identified the problem and are working on a fix. In the meantime, you can try clearing your DNS cache and restarting the service.',
    timestamp: '2024-01-20T14:45:00Z',
    isInternal: false,
    attachments: []
  },
  {
    id: 'R-002',
    ticketId: 'T-002',
    author: 'billing@fendly.com',
    content: 'Thank you for your inquiry. Additional user licenses are $5/month per user. I\'ll send you the activation instructions via email.',
    timestamp: '2024-01-19T16:00:00Z',
    isInternal: false,
    attachments: []
  },
  {
    id: 'R-003',
    ticketId: 'T-003',
    author: 'product@fendly.com',
    content: 'This is a great feature request! We\'ve added it to our roadmap for Q2 2024. I\'ll keep you updated on the progress.',
    timestamp: '2024-01-18T16:30:00Z',
    isInternal: false,
    attachments: []
  }
];

const ticketCategories = [
  { id: 'technical', name: 'Technical Issue', icon: '🔧', color: 'bg-blue-500' },
  { id: 'billing', name: 'Billing & Licensing', icon: '💰', color: 'bg-green-500' },
  { id: 'feature-request', name: 'Feature Request', icon: '💡', color: 'bg-purple-500' },
  { id: 'bug-report', name: 'Bug Report', icon: '🐛', color: 'bg-red-500' },
  { id: 'general', name: 'General Inquiry', icon: '❓', color: 'bg-gray-500' }
];

const priorityLevels = [
  { id: 'low', name: 'Low', color: 'bg-green-100 text-green-800' },
  { id: 'medium', name: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'high', name: 'High', color: 'bg-orange-100 text-orange-800' },
  { id: 'urgent', name: 'Urgent', color: 'bg-red-100 text-red-800' }
];

const statusOptions = [
  { id: 'open', name: 'Open', color: 'bg-blue-100 text-blue-800' },
  { id: 'in-progress', name: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'waiting', name: 'Waiting', color: 'bg-purple-100 text-purple-800' },
  { id: 'resolved', name: 'Resolved', color: 'bg-green-100 text-green-800' },
  { id: 'closed', name: 'Closed', color: 'bg-gray-100 text-gray-800' }
];

export default function Support() {
  const { data: organization } = useUserOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 'medium'
  });

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Organization Required</h2>
          <p className="text-muted-foreground">
            Please set up your organization to access support.
          </p>
        </div>
      </div>
    );
  }

  const filteredTickets = mockTickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = selectedStatus === 'all' || ticket.status === selectedStatus;
    const matchesPriority = selectedPriority === 'all' || ticket.priority === selectedPriority;
    const matchesCategory = selectedCategory === 'all' || ticket.category === selectedCategory;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  const getStatusColor = (status: string) => {
    const statusOption = statusOptions.find(s => s.id === status);
    return statusOption?.color || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string) => {
    const priorityOption = priorityLevels.find(p => p.id === priority);
    return priorityOption?.color || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = ticketCategories.find(c => c.id === categoryId);
    return category?.icon || '❓';
  };

  const getCategoryColor = (categoryId: string) => {
    const category = ticketCategories.find(c => c.id === categoryId);
    return category?.color || 'bg-gray-500';
  };

  const toggleTicket = (ticketId: string) => {
    setExpandedTicket(expandedTicket === ticketId ? null : ticketId);
  };

  const handleCreateTicket = () => {
    // TODO: Implement ticket creation
    console.log('Creating ticket:', newTicket);
    setIsCreatingTicket(false);
    setNewTicket({ title: '', description: '', category: 'general', priority: 'medium' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTicketStats = () => {
    const total = mockTickets.length;
    const open = mockTickets.filter(t => t.status === 'open').length;
    const inProgress = mockTickets.filter(t => t.status === 'in-progress').length;
    const resolved = mockTickets.filter(t => t.status === 'resolved').length;
    const urgent = mockTickets.filter(t => t.priority === 'urgent').length;

    return { total, open, inProgress, resolved, urgent };
  };

  const stats = getTicketStats();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SiteHeader />
      <SidebarInset>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Support Center</h1>
              <p className="text-muted-foreground">
                Get help and manage support tickets for {organization.name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline">
                <Phone className="h-4 w-4 mr-2" />
                Call Support
              </Button>
              <Button onClick={() => setIsCreatingTicket(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Open</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.open}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Resolved</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Urgent</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.urgent}</div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-32">
                  <Eye className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.id} value={status.id}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger className="w-32">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  {priorityLevels.map((priority) => (
                    <SelectItem key={priority.id} value={priority.id}>
                      {priority.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-40">
                  <Tag className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {ticketCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="tickets" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="tickets">My Tickets</TabsTrigger>
              <TabsTrigger value="create">Create Ticket</TabsTrigger>
              <TabsTrigger value="resources">Resources</TabsTrigger>
            </TabsList>

            {/* Tickets Tab */}
            <TabsContent value="tickets" className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Tickets ({filteredTickets.length})</h3>
                <Badge variant="outline">
                  {filteredTickets.filter(t => t.status === 'open').length} Open
                </Badge>
              </div>
              
              {filteredTickets.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No tickets found</h3>
                    <p className="text-muted-foreground">
                      Try adjusting your search criteria or create a new ticket
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {filteredTickets.map((ticket) => (
                    <Card key={ticket.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <CardTitle className="text-lg">{ticket.title}</CardTitle>
                              {ticket.isEscalated && (
                                <Badge variant="default" className="bg-red-500">
                                  Escalated
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="text-base line-clamp-2">
                              {ticket.description}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Badge className={getStatusColor(ticket.status)}>
                              {ticket.status.replace('-', ' ')}
                            </Badge>
                            <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
                              {ticket.priority}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {ticket.createdBy}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(ticket.createdAt)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" />
                              {ticket.responseCount} responses
                            </span>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Button>
                            <Button size="sm" variant="outline">
                              <Reply className="h-4 w-4 mr-2" />
                              Reply
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => toggleTicket(ticket.id)}
                            >
                              {expandedTicket === ticket.id ? 'Show Less' : 'Details'}
                              {expandedTicket === ticket.id ? (
                                <ChevronDown className="h-4 w-4 ml-2" />
                              ) : (
                                <ChevronRight className="h-4 w-4 ml-2" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {expandedTicket === ticket.id && (
                          <div className="mt-4 p-4 bg-muted rounded-lg space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div>
                                <h4 className="font-medium mb-2">Ticket Details</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span>Category:</span>
                                    <Badge variant="outline" className="text-xs">
                                      {getCategoryIcon(ticket.category)} {ticket.category.replace('-', ' ')}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Assigned To:</span>
                                    <span>{ticket.assignedTo || 'Unassigned'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Last Updated:</span>
                                    <span>{formatDate(ticket.updatedAt)}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <h4 className="font-medium mb-2">Tags</h4>
                                <div className="flex flex-wrap gap-1">
                                  {ticket.tags.map((tag, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            {ticket.attachments.length > 0 && (
                              <div>
                                <h4 className="font-medium mb-2">Attachments</h4>
                                <div className="flex gap-2">
                                  {ticket.attachments.map((attachment, index) => (
                                    <Button key={index} size="sm" variant="outline">
                                      <Download className="h-3 w-3 mr-1" />
                                      {attachment}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex gap-2 pt-4 border-t">
                              <Button size="sm" variant="outline">
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Ticket
                              </Button>
                              <Button size="sm" variant="outline">
                                <Forward className="h-4 w-4 mr-2" />
                                Escalate
                              </Button>
                              <Button size="sm" variant="outline">
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Create Ticket Tab */}
            <TabsContent value="create" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Create New Support Ticket</CardTitle>
                  <CardDescription>
                    Submit a new support request. Please provide as much detail as possible to help us assist you quickly.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Title</label>
                    <Input
                      placeholder="Brief description of your issue"
                      value={newTicket.title}
                      onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <Textarea
                      placeholder="Detailed description of your issue, including steps to reproduce, error messages, etc."
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                      rows={6}
                    />
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium">Category</label>
                      <Select value={newTicket.category} onValueChange={(value) => setNewTicket({ ...newTicket, category: value as any })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ticketCategories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.icon} {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Priority</label>
                      <Select value={newTicket.priority} onValueChange={(value) => setNewTicket({ ...newTicket, priority: value as any })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {priorityLevels.map((priority) => (
                            <SelectItem key={priority.id} value={priority.id}>
                              {priority.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleCreateTicket} disabled={!newTicket.title || !newTicket.description}>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Ticket
                    </Button>
                    <Button variant="outline" onClick={() => setNewTicket({ title: '', description: '', category: 'general', priority: 'medium' })}>
                      Clear Form
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Resources Tab */}
            <TabsContent value="resources" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <HelpCircle className="h-12 w-12 mx-auto mb-4 text-blue-500" />
                    <h3 className="text-lg font-semibold mb-2">Help Center</h3>
                    <p className="text-muted-foreground mb-4">Browse help articles and tutorials</p>
                    <Button size="sm" className="w-full">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visit Help Center
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <h3 className="text-lg font-semibold mb-2">Knowledge Base</h3>
                    <p className="text-muted-foreground mb-4">Detailed documentation and guides</p>
                    <Button size="sm" variant="outline" className="w-full">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Browse KB
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 text-purple-500" />
                    <h3 className="text-lg font-semibold mb-2">Live Chat</h3>
                    <p className="text-muted-foreground mb-4">Get instant help from support team</p>
                    <Button size="sm" variant="outline" className="w-full">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Start Chat
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <Phone className="h-12 w-12 mx-auto mb-4 text-orange-500" />
                    <h3 className="text-lg font-semibold mb-2">Phone Support</h3>
                    <p className="text-muted-foreground mb-4">Speak with an expert directly</p>
                    <Button size="sm" variant="outline" className="w-full">
                      <Phone className="h-4 w-4 mr-2" />
                      Call Now
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <Mail className="h-12 w-12 mx-auto mb-4 text-red-500" />
                    <h3 className="text-lg font-semibold mb-2">Email Support</h3>
                    <p className="text-muted-foreground mb-4">Send us a detailed message</p>
                    <Button size="sm" variant="outline" className="w-full">
                      <Mail className="h-4 w-4 mr-2" />
                      Send Email
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <Download className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                    <h3 className="text-lg font-semibold mb-2">Downloads</h3>
                    <p className="text-muted-foreground mb-4">Manuals, guides, and tools</p>
                    <Button size="sm" variant="outline" className="w-full">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
